import Styles from './styles.module.css';
import React, { useRef, useState } from 'react';
import { getFirestore, addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';
import { v4 as uuid } from 'uuid';
import { IoImageOutline } from 'react-icons/io5';

export type CouponEditing = {
  id: string;
  docId: string;
  image: boolean;
  treatment: string;
  description: string;
  price: { before: string; after: string };
  contact: { phone: string };
};

type CouponMakeProps = {
  companyId: string;
  setMake: (open: boolean) => void;
  // 渡したときは、そのクーポンの編集。画像は変えたときだけ差し替える。
  editing?: CouponEditing | null;
};

const CouponMake: React.FC<CouponMakeProps> = ({ companyId, setMake, editing = null }) => {

  const db = getFirestore();

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [ treatment, setTreatment ] = useState<string>(editing?.treatment ?? '');
  const [ description, setDescription ] = useState<string>(editing?.description ?? '');
  const [ priceBefore, setPriceBefore ] = useState<string>(editing?.price?.before != null ? String(editing.price.before) : '');
  const [ priceAfter, setPriceAfter ] = useState<string>(editing?.price?.after != null ? String(editing.price.after) : '');
  const [ phone, setPhone ] = useState<string>(editing?.contact?.phone ?? '');
  const [ imagePreview, setImagePreview ] = useState<string>(
    editing?.image && editing.docId ? `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/coupons%2F${editing.docId}?alt=media` : '',
  );
  const [ imageFile, setImageFile ] = useState<File | null>(null);

  const [ errorImage, setErrorImage ] = useState(false);
  const [ errorTreatment, setErrorTreatment ] = useState(false);
  const [ errorPriceBefore, setErrorPriceBefore ] = useState(false);
  const [ errorPriceAfter, setErrorPriceAfter ] = useState(false);
  const [ errorPriceCompare, setErrorPriceCompare ] = useState(false);
  const [ uploading, setUploading ] = useState(false);

  // 保存時のバリデーション結果を1件だけ上部に表示する
  const [ errorMessage, setErrorMessage ] = useState<string>('');

  const showError = (message: string) => {
    setErrorMessage(message);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setImageFile(e.target.files[0]);
    }
  };

  async function save() {

    setErrorImage(false);
    setErrorTreatment(false);
    setErrorPriceBefore(false);
    setErrorPriceAfter(false);
    setErrorPriceCompare(false);
    setErrorMessage('');

    if (!imageFile && !(editing?.image)) {
      setErrorImage(true);
      showError('施術画像を添付してください。');
    } else if (treatment === '') {
      setErrorTreatment(true);
      showError('施術名を入力してください。');
    } else if (priceBefore === '') {
      setErrorPriceBefore(true);
      showError('通常料金を入力してください。');
    } else if (priceAfter === '') {
      setErrorPriceAfter(true);
      showError('美容学生料金を入力してください。');
    } else if (Number(priceAfter) > Number(priceBefore)) {
      setErrorPriceCompare(true);
      showError('美容学生料金が通常料金より高くなっています。');
    } else {
      try {
        setUploading(true);

        if (editing) {
          // 画像を変えたときは新しい名前で保存し直す(同じ名前で上書きすると古い画像が残って見えるため)
          const storage = getStorage();
          let docId = editing.docId;
          if (imageFile) {
            docId = uuid();
            await uploadBytes(ref(storage, `coupons/${docId}`), imageFile);
          }
          await updateDoc(doc(db, 'coupons', editing.id), {
            docId,
            treatment,
            description,
            price: { before: priceBefore, after: priceAfter },
            contact: { phone },
            image: imageFile ? true : editing.image,
            updated_at: new Date(),
          });
          if (imageFile && editing.image && editing.docId) {
            await Promise.all([
              deleteObject(ref(storage, `coupons/${editing.docId}`)),
              deleteObject(ref(storage, `coupons/resize_images/${editing.docId}_500x500`)),
            ].map((p) => p.catch(() => undefined)));
          }
          setMake(false);
          return;
        }

        const docId = uuid();

        if (imageFile) {
          const storage = getStorage();
          const imageRef = ref(storage, `coupons/${docId}`);
          await uploadBytes(imageRef, imageFile);
        }

        await addDoc(collection(db, 'coupons'), {
          companyId: companyId,
          docId: docId,
          treatment: treatment,
          description: description,
          price: {
            before: priceBefore,
            after: priceAfter,
          },
          contact: {
            phone: phone,
          },
          image: imageFile ? true : false,
          time: new Date(),
        });

        setMake(false);
      } catch (error) {
        console.error('エラーが発生しました', error);
        setUploading(false);
      }
    }

  }

  const inputClass = (hasError: boolean) =>
    hasError ? `${Styles.input} ${Styles.input_error}` : Styles.input;

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true'>
      <div className={Styles.scrim} onClick={() => !uploading && setMake(false)} />
      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>{editing ? 'クーポンの編集' : 'クーポンの追加'}</h3>
          <button type='button' className={Styles.headerBtn} onClick={() => save()} disabled={uploading}>
            {uploading ? '保存中…' : editing ? '保存' : '追加'}
          </button>
        </div>

        <div className={Styles.body} ref={bodyRef}>

          {errorMessage && (
            <div className={Styles.errorBox} role='alert'>
              <p className={Styles.errorBox_label}>エラーメッセージ：</p>
              <p className={Styles.errorBox_text}>{errorMessage}</p>
            </div>
          )}

          {/* 施術画像 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>施術画像<span className={Styles.req}>※</span></span>
            </div>
            <label className={errorImage ? `${Styles.imageBox} ${Styles.imageBox_error}` : Styles.imageBox}>
              {imagePreview ? (
                <img className={Styles.imagePreview} src={imagePreview} alt='施術画像' />
              ) : (
                <div className={Styles.imagePlaceholder}>
                  <IoImageOutline className={Styles.imagePlaceholderIcon} />
                  <span>画像を追加</span>
                </div>
              )}
              <input type='file' accept='image/*' onChange={handleImageChange} disabled={uploading} className={Styles.hiddenInput} />
            </label>
          </div>

          {/* 施術名 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>施術名<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{treatment.length} / 30</span>
            </div>
            <input
              className={inputClass(errorTreatment)}
              placeholder='カット＋カラー'
              maxLength={30}
              type='text'
              value={treatment}
              onChange={(e) => setTreatment(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* 補足・説明 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>補足・説明<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{description.length} / 100</span>
            </div>
            <textarea
              className={`${Styles.input} ${Styles.textarea}`}
              placeholder='施術の詳細や条件などを入力してください'
              maxLength={100}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* 通常料金 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>通常料金<span className={Styles.req}>※</span></span>
            </div>
            <input
              className={inputClass(errorPriceBefore)}
              placeholder='5000'
              type='text'
              value={priceBefore}
              onChange={(e) => setPriceBefore(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* 美容学生料金 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>美容学生料金<span className={Styles.req}>※</span></span>
            </div>
            <input
              className={inputClass(errorPriceAfter || errorPriceCompare)}
              placeholder='2000'
              type='text'
              value={priceAfter}
              onChange={(e) => setPriceAfter(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* 電話番号 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>電話番号<span className={Styles.optional}>任意</span></span>
            </div>
            <input
              className={Styles.input}
              placeholder='03-0000-0000'
              type='text'
              maxLength={13}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={uploading}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

export default CouponMake;
